import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { LuCalendarDays, LuChevronLeft, LuChevronRight, LuClock } from "react-icons/lu";
import { motion } from "motion/react";
import * as chrono from "chrono-node";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  loading?: boolean;
};

export function Button({
  variant = "primary",
  loading = false,
  className = "",
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";
  const variants = {
    primary: "bg-brand-600 text-white hover:bg-brand-700 shadow-sm",
    secondary: "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-sm",
    ghost: "text-slate-600 hover:bg-slate-100",
    danger: "bg-rose-600 text-white hover:bg-rose-700 shadow-sm",
  };
  return (
    <motion.button
      {...(rest as object)}
      whileTap={disabled || loading ? undefined : { scale: 0.96 }}
      disabled={disabled || loading}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {loading && (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </motion.button>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  const { className = "", ...rest } = props;
  return (
    <input
      {...rest}
      className={`w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-brand-500 focus:ring-2 focus:ring-brand-200 ${className}`}
    />
  );
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className = "", ...rest } = props;
  return (
    <textarea
      {...rest}
      className={`w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-brand-500 focus:ring-2 focus:ring-brand-200 ${className}`}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const { className = "", children, ...rest } = props;
  const hasWidth = /(?:^|\s)w-/.test(className);
  return (
    <select
      {...rest}
      className={`${hasWidth ? "" : "w-full "}rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-200 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </select>
  );
}

type IconButtonVariant = "default" | "danger" | "warn";

export function IconButton({
  icon,
  label,
  onClick,
  variant = "default",
  active = false,
  disabled = false,
  type = "button",
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  variant?: IconButtonVariant;
  active?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  const variants: Record<IconButtonVariant, string> = {
    default: "text-slate-400 hover:bg-slate-100 hover:text-slate-700",
    danger: "text-slate-400 hover:bg-rose-50 hover:text-rose-600",
    warn: "text-slate-400 hover:bg-amber-50 hover:text-amber-500",
  };
  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      whileTap={disabled ? undefined : { scale: 0.88 }}
      className={`rounded-md p-1.5 transition disabled:cursor-not-allowed disabled:opacity-50 ${active ? "text-emerald-600" : variants[variant]}`}
    >
      {icon}
    </motion.button>
  );
}

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-slate-600">
      {children}
    </label>
  );
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      {message}
    </div>
  );
}

export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ size = "md" }: { size?: "sm" | "md" }) {
  const dim = size === "sm" ? "h-4 w-4" : "h-6 w-6";
  return (
    <span
      className={`inline-block ${dim} animate-spin rounded-full border-2 border-brand-500 border-t-transparent`}
    />
  );
}

export function CenteredSpinner() {
  return (
    <div className="flex items-center justify-center py-10">
      <Spinner />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Pill({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "slate" | "emerald" | "amber" | "rose" | "brand" | "violet";
}) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    brand: "bg-brand-100 text-brand-800",
    violet: "bg-violet-100 text-violet-800",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

// ─── DateTimePicker ───────────────────────────────────────────────────────────

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const WEEKDAYS = ["Mo","Tu","We","Th","Fr","Sa","Su"];

function dtlFormat(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function displayFormat(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    + " · "
    + String(d.getHours()).padStart(2, "0")
    + ":"
    + String(d.getMinutes()).padStart(2, "0");
}

function calendarDays(year: number, month: number) {
  const firstDow = new Date(year, month, 1).getDay(); // 0 = Sun
  const offset = (firstDow + 6) % 7; // convert to Mon-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { year: number; month: number; day: number; cur: boolean }[] = [];

  for (let i = offset - 1; i >= 0; i--)
    cells.push({ year: month === 0 ? year - 1 : year, month: month === 0 ? 11 : month - 1, day: daysInPrev - i, cur: false });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ year, month, day: d, cur: true });
  const tail = 42 - cells.length;
  for (let d = 1; d <= tail; d++)
    cells.push({ year: month === 11 ? year + 1 : year, month: month === 11 ? 0 : month + 1, day: d, cur: false });

  return cells;
}

export function DateTimePicker({
  id,
  value,
  onChange,
  disabled,
  placeholder = "Pick a due date",
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const parsed = value ? new Date(value) : null;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const d = parsed ?? new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Keep view in sync when value changes externally
  useEffect(() => {
    if (parsed) setView({ year: parsed.getFullYear(), month: parsed.getMonth() });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const hour = parsed ? parsed.getHours() : 0;
  const minute = parsed ? parsed.getMinutes() : 0;
  const today = new Date();

  function pickDay(year: number, month: number, day: number) {
    const d = new Date(year, month, day, hour, minute);
    onChange(dtlFormat(d));
    setView({ year, month });
  }

  function setTime(h: number, m: number) {
    const base = parsed ?? new Date(view.year, view.month, 1);
    onChange(dtlFormat(new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, m)));
  }

  function prevMonth() {
    setView((v) => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  }
  function nextMonth() {
    setView((v) => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 });
  }

  const cells = calendarDays(view.year, view.month);

  return (
    <div ref={ref} className="relative w-full">
      {/* Trigger */}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition
          ${open ? "border-brand-500 ring-2 ring-brand-200" : "hover:border-slate-300"}
          disabled:cursor-not-allowed disabled:opacity-50`}
      >
        <span className={parsed ? "text-slate-900" : "text-slate-400"}>
          {parsed ? displayFormat(parsed) : placeholder}
        </span>
        <LuCalendarDays className="h-4 w-4 shrink-0 text-slate-400" />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
          {/* Month nav */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={prevMonth}
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <LuChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-slate-900">
              {MONTHS[view.month]} {view.year}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <LuChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="mb-1 grid grid-cols-7">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((cell, i) => {
              const isSelected =
                parsed &&
                cell.year === parsed.getFullYear() &&
                cell.month === parsed.getMonth() &&
                cell.day === parsed.getDate();
              const isToday =
                cell.year === today.getFullYear() &&
                cell.month === today.getMonth() &&
                cell.day === today.getDate();
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickDay(cell.year, cell.month, cell.day)}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs transition
                    ${isSelected
                      ? "bg-brand-600 font-semibold text-white"
                      : isToday
                      ? "border border-brand-300 font-medium text-brand-700 hover:bg-brand-50"
                      : cell.cur
                      ? "text-slate-800 hover:bg-slate-100"
                      : "text-slate-300 hover:bg-slate-50"
                    }`}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          {/* Time picker */}
          <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
            <LuClock className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              type="number"
              min={0}
              max={23}
              value={String(hour).padStart(2, "0")}
              onChange={(e) => {
                const h = Math.min(23, Math.max(0, Number(e.target.value)));
                setTime(h, minute);
              }}
              className="w-12 rounded-md border border-slate-200 bg-white px-2 py-1 text-center text-sm font-medium text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
            <span className="text-sm font-medium text-slate-500">:</span>
            <input
              type="number"
              min={0}
              max={59}
              step={5}
              value={String(minute).padStart(2, "0")}
              onChange={(e) => {
                const m = Math.min(59, Math.max(0, Number(e.target.value)));
                setTime(hour, m);
              }}
              className="w-12 rounded-md border border-slate-200 bg-white px-2 py-1 text-center text-sm font-medium text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto rounded-lg bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NlpDateInput ─────────────────────────────────────────────────────────────

export function NlpDateInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState("");
  const hasText = text.trim().length > 0;
  const parsed = hasText ? chrono.parseDate(text, new Date(), { forwardDate: true }) : null;
  const isError = hasText && !parsed;
  const committed = value ? new Date(value) : null;

  const commit = () => {
    if (parsed) {
      const p = (n: number) => String(n).padStart(2, "0");
      const iso = `${parsed.getFullYear()}-${p(parsed.getMonth() + 1)}-${p(parsed.getDate())}T${p(parsed.getHours())}:${p(parsed.getMinutes())}`;
      onChange(iso);
      setText("");
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && parsed) {
      e.preventDefault();
      flushSync(() => commit());
      e.currentTarget.form?.requestSubmit();
    }
    if (e.key === "Escape") setText("");
  };

  if (committed && !hasText) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm">
        <span className="font-medium text-indigo-700">
          {committed.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
          {" · "}
          {committed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
        </span>
        <button
          type="button"
          onClick={() => onChange("")}
          disabled={disabled}
          className="text-indigo-400 hover:text-indigo-700 disabled:opacity-50"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        disabled={disabled}
        placeholder='e.g. "tomorrow", "next Friday"'
        className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 disabled:opacity-50 ${
          isError
            ? "border-red-300 focus:ring-red-400"
            : "border-slate-200 focus:ring-indigo-500"
        }`}
      />
      {parsed && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-indigo-500 pointer-events-none">
          {parsed.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
          {" · "}
          {String(parsed.getHours()).padStart(2, "0")}:{String(parsed.getMinutes()).padStart(2, "0")}
        </span>
      )}
      {isError && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-400 pointer-events-none">
          can't parse
        </span>
      )}
    </div>
  );
}
